import React from 'react';
import {Navigate, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {paginaDelMenu, tornei} from '../../moduli/leggi';
import {useSkin} from '../../moduli/useSkin';
import {indirizzoImmagine} from '../../utils/immagini';
import Nota from '../Vetrina/Nota';
import './Pagine.css';

/* Lo sfondo di un torneo: l'immagine della skin, o la sfumatura di Pagine.css. */
function sfondo(immagine) {
  return immagine ? {backgroundImage: 'url("' + indirizzoImmagine(immagine) + '")'} : undefined;
}

/* Un dato del torneo (Tipo, Montepremi, Termina tra): etichetta sopra, valore sotto. Vuoto non c'e'. */
function Dato({nome, valore}) {
  if (!valore) return null;
  return <span className={'pg-dato'}><span className={'pg-dato-nome'}>{nome}</span><span className={'pg-dato-valore'}>{valore}</span></span>;
}

/**
 * LA CLASSIFICA, SPENTA DI SERIE: la dara' il backend (classifica del
 * torneo e posizione del giocatore, nel documento dell'API). Accesa senza
 * backend, al suo posto c'e' lo scheletro: righe vuote tratteggiate e il
 * riquadro «La tua posizione» senza numero. Niente di inventato.
 */
function Classifica({nota}) {
  const {t} = useTranslation();
  return (
    <div className={'pg-classifica-riga'}>
      <section className={'pg-classifica'} aria-label={t('Classifica')}>
        <div className={'pg-classifica-testa'}><strong>{t('Classifica')}</strong>{nota && <span>{nota}</span>}</div>
        <ul className={'pg-classifica-scheletro'} aria-busy={'true'} aria-label={t('Classifica in arrivo')}>
          {[0, 1, 2, 3, 4, 5].map((i) => <li key={i} className={'pg-classifica-vuota'}/>)}
        </ul>
      </section>
      <aside className={'pg-posizione'} aria-label={t('La tua posizione')}>
        <strong>{t('La tua posizione')}</strong>
        <span className={'pg-posizione-numero'}>—</span>
        <span className={'pg-posizione-barra'}><span style={{width: 0}}/></span>
        <Nota tono={'spento'} piccola giocatore={t('La classifica arriva a breve.')}>
          {t('Classifica e posizione del giocatore le dà il backend (API dei tornei): finché non c’è, qui resta lo scheletro.')}
        </Nota>
      </aside>
    </div>
  );
}

/**
 * LA PAGINA TORNEI (modulo Pagine › Tornei, /tornei): i tornei in corso uno
 * sotto l'altro, ognuno in un riquadro grande con lo sfondo, le etichette,
 * la cadenza, il nome e «DAL VIVO»; sotto, la striscia con tipo,
 * montepremi, «termina tra», i collegamenti e il pulsante «Partecipa». Poi
 * la classifica (solo con l'interruttore acceso) e i tornei completati in
 * schede, quattro per riga dal computer, due sul tablet, una sul telefono.
 *
 * Spenta, l'indirizzo porta alla Home. Senza tornei scritti resta il posto
 * vuoto, con scritto perche' solo in anteprima.
 */
export default function Tornei() {
  useSkin();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const pagina = paginaDelMenu('tornei');
  const p = tornei();
  if (!pagina || !pagina.acceso) return <Navigate to={'/'} replace/>;
  const titolo = pagina.titolo === pagina.titoloDiSerie ? t('Tornei') : pagina.titolo;
  const vai = (dove) => {
    if (!dove) return;
    if (/^https?:\/\//i.test(dove)) window.open(dove, '_blank', 'noopener');
    else navigate(dove);
  };
  const vuota = !p.inCorso.length && !p.completati.length;
  return (
    <div className={'vetrina pg-tornei'}>
      <h1 className={'pg-titolo'}>{titolo}</h1>
      {vuota && (
        <Nota tono={'spento'} giocatore={t('Non ci sono tornei in questo momento.')}>
          {t('I tornei si scrivono dal backoffice (Moduli › Pagine › Tornei). Oggi non ce n’è ancora nessuno.')}
        </Nota>
      )}
      {p.inCorso.length > 0 && (
        <section className={'pg-corpo'} aria-label={p.inCorsoTitolo || t('In corso')}>
          <h2 className={'pg-sotto'}>{p.inCorsoTitolo || t('In corso')}</h2>
          {p.inCorso.map((r, i) => (
            <React.Fragment key={i}>
              <article className={'pg-torneo'} aria-label={r.nome}>
                <div className={'pg-torneo-sfondo' + (r.immagine ? ' con-immagine' : '')} style={sfondo(r.immagine)}>
                  {r.etichette.length > 0 && (
                    <div className={'pg-torneo-etichette'}>{r.etichette.map((e) => <span key={e}>{e}</span>)}</div>
                  )}
                  <div className={'pg-torneo-centro'}>
                    {r.cadenza && <span className={'pg-torneo-cadenza'}>{r.cadenza}</span>}
                    <h3>{r.nome}</h3>
                    <span className={'pg-torneo-vivo'}>{t('DAL VIVO')}</span>
                  </div>
                </div>
                <div className={'pg-torneo-striscia'}>
                  <span className={'pg-torneo-gemma'} aria-hidden={'true'}/>
                  <Dato nome={t('Tipo')} valore={r.tipo}/>
                  <Dato nome={t('Montepremi')} valore={r.montepremi}/>
                  <Dato nome={t('Termina tra')} valore={r.termina}/>
                  {r.collegamento && <button type={'button'} className={'pg-collegamento'} onClick={() => vai(r.collegamento)}>{t('Leggi di più')}</button>}
                  {r.giochi && <button type={'button'} className={'pg-collegamento'} onClick={() => vai(r.giochi)}>{t('Giochi')} ›</button>}
                  {r.partecipa && (
                    <button type={'button'} className={'pg-oro pg-torneo-partecipa'} onClick={() => vai(r.partecipa)}
                      aria-label={(r.pulsante || t('Partecipa')) + ': ' + r.nome}>{r.pulsante || t('Partecipa')}</button>
                  )}
                </div>
                {r.regola && <p className={'pg-torneo-regola'}>{r.regola}</p>}
              </article>
              {p.classifica && <Classifica nota={p.classificaNota}/>}
            </React.Fragment>
          ))}
        </section>
      )}
      {p.completati.length > 0 && (
        <section className={'pg-corpo'} aria-label={p.completatiTitolo || t('Completati')}>
          <h2 className={'pg-sotto'}>{p.completatiTitolo || t('Completati')}</h2>
          <div className={'pg-finiti'}>
            {p.completati.map((r, i) => (
              <article key={i} className={'pg-finito'} aria-label={r.nome}>
                <div className={'pg-finito-sfondo' + (r.immagine ? ' con-immagine' : '')} style={Object.assign({'--i': i}, sfondo(r.immagine))}>
                  {r.cadenza && <span className={'pg-torneo-cadenza'}>{r.cadenza}</span>}
                  <h3>{r.nome}</h3>
                  <span className={'pg-finito-finito'}>{t('FINITO')}</span>
                </div>
                <div className={'pg-finito-dati'}>
                  <Dato nome={t('Tipo')} valore={r.tipo}/>
                  <Dato nome={t('Montepremi')} valore={r.montepremi}/>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
